import React from "react";
import styled, { css } from "styled-components";
import { Spacer } from "../Spacer";
import { Tooltip } from "../Tooltip";
import { style } from "../style";

type InputLabelProps = {
  text: string;
  size: "small" | "base";
  subtext?: React.ReactNode;
  tooltip?: string;

  htmlFor?: string;
  as?: keyof JSX.IntrinsicElements;
  required?: boolean;
  invalid?: boolean;
};

type TextProps = {
  $required: boolean;
  $invalid: boolean;
  $size: "small" | "base";
};

export const Container = styled.label`
  all: unset;
`;

const Text = styled.span<TextProps>`
  color: ${style.colors.grey.darkest};
  font-weight: ${style.vars.fontWeight.bold};
  font-size: ${(props) => style.vars.fontSize[props.$size]};

  ${(props) => {
    if (props.$required) {
      const color = props.$invalid ? style.colors.red.dark : style.colors.blue.primary;

      return css`
        &:after {
          font-size: ${style.vars.fontSize.small};
          color: ${color};
          content: "*";
        }
      `;
    }
  }}
`;

const Subtext = styled.div`
  font-size: ${style.vars.fontSize.small};
  color: ${style.colors.grey.dark};
`;

const Label: React.FC<InputLabelProps> = (props) => {
  const { text, htmlFor, size, subtext = null, tooltip = null, as, required = false, invalid = false } = props;

  return (
    <>
      <Container htmlFor={htmlFor} as={as}>
        <Text $size={size} $required={required} $invalid={invalid}>
          {text}
        </Text>
        {tooltip && <Tooltip.Help content={tooltip} />}
      </Container>
      {subtext && <Subtext>{subtext}</Subtext>}
    </>
  );
};

Label.displayName = "InputLabel";

export type PropLabel = string | { text: string; subtext?: React.ReactNode; tooltip?: string };

type RenderForBeforeInputOptions = {
  required: boolean;
  invalid: boolean;
  as: keyof JSX.IntrinsicElements;
  htmlFor: string;
};

/**
 * Functions for taking a `PropLabel` and rendering it in a consistent way. In order to use,
 * a component should include a prop `label?: PropLabel` and then call `PropLabel.renderForBeforeInput(props.label, { ... })`.
 * This ensures that there are only a few ways to render a label, but that all of the inputs are consistent.
 */
export const PropLabel = {
  isEqual(a: PropLabel | undefined, b: PropLabel | undefined): boolean {
    if (a === undefined && b === undefined) {
      return true;
    }

    if (typeof a === "string" && typeof b === "string") {
      return a === b;
    }

    if (typeof a === "object" && typeof b === "object") {
      return a.text === b.text && a.subtext === b.subtext && a.tooltip === b.tooltip;
    }

    return false;
  },

  renderForBeforeInput(label: PropLabel, options: Partial<RenderForBeforeInputOptions> = {}): JSX.Element {
    if (typeof label === "string") {
      label = { text: label };
    }

    return (
      <>
        <Label size="small" {...options} {...label} />
        <Spacer space={2} />
      </>
    );
  },

  renderForBeforeFormSection(label: PropLabel, options: Partial<RenderForBeforeInputOptions> = {}): JSX.Element {
    if (typeof label === "string") {
      label = { text: label };
    }

    return (
      <>
        <Label size="base" {...options} {...label} />
        <Spacer space={4} />
      </>
    );
  },
};

export type PublicPropLabel = PropLabel;

export const PublicPropLabel: Pick<typeof PropLabel, "renderForBeforeInput" | "renderForBeforeFormSection"> = {
  renderForBeforeInput: PropLabel.renderForBeforeInput,
  renderForBeforeFormSection: PropLabel.renderForBeforeFormSection,
};
